import {
    ContentPropertiesManager,
    CreativeTypes,
    IAssetsLoader,
    PagePreviewRenderer,
    ShotRenderer,
    TextRenderer,
    TextStyles,
    ValidationManagerForMultiPage,
    VariationThumbnailRenderer,
    type VideoModel,
    VideoWebSpecificationParser,
} from '@bynder-studio/render-core';
import hbWasmURL from '@bynder-studio/hbjs/hb.wasm';
import { setFontFamilies } from '@bynder-studio/structured-text';
import { BrowserAssetsLoader } from '../AssetLoader/BrowserAssetsLoader';
import { CanvasLayerCompositor } from '../Compositor/CanvasLayerCompositor/CanvasLayerCompositor';
import CanvasManipulation from '../Compositor/CanvasManipulationCompositor/CanvasManipulation';
import CanvasManipulationCompositor from '../Compositor/CanvasManipulationCompositor/CanvasManipulationCompositor';
import CanvasShotCompositor from '../Compositor/CanvasShotCompositor/CanvasShotCompositor';
import CanvasPagePreviewCompositor from '../Compositor/CanvasPagePreviewCompositor/CanvasPagePreviewCompositor';
import { PlaybackManipulationRenderer } from '../Renderers/PlaybackManipulationRenderer/PlaybackManipulationRenderer';
import { PlaybackRenderer } from '../Renderers/PlaybackRenderer/PlaybackRenderer';
import { CreativeModelFactoryConfig } from './types';
import CanvasPreviewManipulation from '../Compositor/CanvasManipulationCompositor/CanvasPreviewManipulation';

export const videoModelFactory = async ({
    template,
    defaultFontBlob,
    fontFamilies,
    textStyles,
    contentProperties,
    previewType,
    containerDocument,
}: CreativeModelFactoryConfig) => {
    // todo: improve
    const assetLoader = new BrowserAssetsLoader();
    const devicePixelRatio = window.devicePixelRatio;

    const loopInterval = (callback) => window.requestAnimationFrame(callback);

    setFontFamilies(fontFamilies);

    const hbWasmFile: any = await fetch(hbWasmURL).then((res) => res.arrayBuffer());
    (await TextRenderer.init(hbWasmFile)).setDefaultFont(defaultFontBlob);

    const textStylesInstance = new TextStyles();
    const contentPropertiesManager = new ContentPropertiesManager();
    const videoWebSpecificationParser = new VideoWebSpecificationParser();

    textStylesInstance.loadStyles(textStyles);
    contentPropertiesManager.loadContentProperties(
        VideoWebSpecificationParser.parseContentProperties(contentProperties, fontFamilies, template),
    );

    const multiPageVideoModel = videoWebSpecificationParser
        .parseMultiPage(template, fontFamilies)
        .setAssetLoader(assetLoader as IAssetsLoader)
        .setTextStyles(textStylesInstance)
        .setContentPropertiesManager(contentPropertiesManager)
        .setup();

    const validationManager = new ValidationManagerForMultiPage(multiPageVideoModel, CreativeTypes.VIDEO, previewType);

    return {
        setFontFamilies,
        getCreativeModel: () => multiPageVideoModel,
        getAssetLoader: () => assetLoader,
        getValidationManager: () => validationManager,
        getCreativeRenderer: (canvasWrapperEl: HTMLElement) => {
            const canvasCompositor = new CanvasLayerCompositor(
                canvasWrapperEl,
                assetLoader as IAssetsLoader,
                devicePixelRatio,
            );

            return new PlaybackRenderer(
                multiPageVideoModel,
                assetLoader as IAssetsLoader,
                canvasCompositor,
                loopInterval,
            );
        },
        getCreativeManipulationRenderer: (canvasWrapperEl) => {
            const canvasCompositor = new CanvasManipulationCompositor(
                canvasWrapperEl,
                assetLoader as IAssetsLoader,
                devicePixelRatio,
            );
            const canvasManipulation = new CanvasManipulation(canvasCompositor, containerDocument, validationManager);

            return new PlaybackManipulationRenderer(
                multiPageVideoModel,
                assetLoader as IAssetsLoader,
                canvasCompositor,
                canvasManipulation,
                loopInterval,
            );
        },
        getCreativePreviewManipulationRenderer: (canvasWrapperEl) => {
            const canvasCompositor = new CanvasManipulationCompositor(
                canvasWrapperEl,
                assetLoader as IAssetsLoader,
                devicePixelRatio,
            );
            const canvasManipulation = new CanvasPreviewManipulation(canvasCompositor, containerDocument);

            return new PlaybackManipulationRenderer(
                multiPageVideoModel,
                assetLoader as IAssetsLoader,
                canvasCompositor,
                canvasManipulation as unknown as CanvasManipulation,
                loopInterval,
            );
        },
        getShotRenderer: () => {
            const canvasShotCompositor = new CanvasShotCompositor(assetLoader as IAssetsLoader, devicePixelRatio);

            return new ShotRenderer(
                multiPageVideoModel as unknown as VideoModel,
                assetLoader as IAssetsLoader,
                canvasShotCompositor,
            );
        },
        getPagePreviewRenderer: () => {
            const canvasPagePreviewCompositor = new CanvasPagePreviewCompositor(
                assetLoader as IAssetsLoader,
                devicePixelRatio,
            );

            return new PagePreviewRenderer(
                multiPageVideoModel,
                assetLoader as IAssetsLoader,
                canvasPagePreviewCompositor,
            );
        },
        getVariationThumbnailRenderer: () => {
            const canvasVariationThumbnailCompositor = new CanvasPagePreviewCompositor(
                assetLoader as IAssetsLoader,
                devicePixelRatio,
            );

            return new VariationThumbnailRenderer(assetLoader as IAssetsLoader, canvasVariationThumbnailCompositor);
        },
    };
};
